import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import StaticPageLayout from '../../components/StaticPageLayout';
import { AI_EMPLOYEES, EMPLOYEE_CATEGORIES } from '../../data/employees';
import { useThemeStore } from '../../store/theme';

export default function AIEmployees() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  return (
    <StaticPageLayout
      title="Meet Your AI Team"
      subtitle="15 specialized AI employees ready to work for your business 24/7."
    >
      {/* Categories */}
      <section className="mb-12">
        <div className="flex flex-wrap gap-3 justify-center">
          {EMPLOYEE_CATEGORIES.map((category) => (
            <a
              key={category.id}
              href={`#${category.id}`}
              className={`px-4 py-2 rounded-full border transition-all ${
                isDark
                  ? 'bg-dark-800 border-white/10 text-gray-300 hover:border-purple-500/50 hover:text-white'
                  : 'bg-white border-slate-200 text-slate-600 hover:border-purple-300 hover:text-purple-600 shadow-sm'
              }`}
            >
              {category.name}
            </a>
          ))}
        </div>
      </section>

      {/* Employee Grid by Category */}
      {EMPLOYEE_CATEGORIES.map((category) => {
        const categoryEmployees = AI_EMPLOYEES.filter((e) => e.category === category.id);
        if (categoryEmployees.length === 0) return null;

        return (
          <section key={category.id} id={category.id} className="mb-16">
            <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>{category.name}</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryEmployees.map((employee, index) => (
                <motion.div
                  key={employee.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`group relative p-6 rounded-2xl border transition-all ${
                    isDark
                      ? 'bg-dark-800/50 border-white/5 hover:border-white/20'
                      : 'bg-white border-slate-200 hover:border-purple-300 shadow-sm hover:shadow-md'
                  }`}
                >
                  {/* Badges */}
                  <div className="absolute top-4 right-4 flex gap-2">
                    {employee.isNew && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
                        isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600'
                      }`}>
                        <Sparkles className="w-3 h-3" />
                        New
                      </span>
                    )}
                  </div>

                  {/* Avatar */}
                  <div
                    className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${employee.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
                  >
                    <span className="text-3xl">{employee.avatar}</span>
                  </div>

                  {/* Info */}
                  <h3 className={`text-xl font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>{employee.name}</h3>
                  <p className={`text-sm mb-3 ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>{employee.role}</p>
                  <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{employee.description}</p>

                  {/* Capabilities */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {employee.capabilities.slice(0, 3).map((cap) => (
                      <span
                        key={cap}
                        className={`px-2 py-1 rounded-lg text-xs ${
                          isDark ? 'bg-dark-700 text-gray-400' : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {cap}
                      </span>
                    ))}
                    {employee.capabilities.length > 3 && (
                      <span className={`px-2 py-1 rounded-lg text-xs ${
                        isDark ? 'bg-dark-700 text-gray-500' : 'bg-slate-100 text-slate-500'
                      }`}>
                        +{employee.capabilities.length - 3} more
                      </span>
                    )}
                  </div>

                  {/* CTA */}
                  <Link
                    to={`/register?employee=${employee.id}`}
                    className={`flex items-center gap-2 text-sm font-medium ${
                      isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'
                    }`}
                  >
                    Start chatting <ArrowRight className="w-4 h-4" />
                  </Link>
                </motion.div>
              ))}
            </div>
          </section>
        );
      })}

      {/* CTA */}
      <section className={`text-center p-8 rounded-2xl border ${
        isDark
          ? 'bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border-white/5'
          : 'bg-gradient-to-br from-purple-50 to-cyan-50 border-purple-100'
      }`}>
        <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Ready to Build Your AI Team?</h2>
        <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
          Get started for free and experience the future of work.
        </p>
        <Link
          to="/register"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl font-medium text-white shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow"
        >
          Get Started Free
        </Link>
      </section>
    </StaticPageLayout>
  );
}
